'use client'

import {
  Thermometer,
  Crown,
  Smile,
  GraduationCap,
  Compass,
  Handshake,
  ConciergeBell,
  Trophy,
  Heart,
  AlertTriangle,
  MessageCircle,
  Gauge,
} from 'lucide-react'

const brandColors = {
  primary: '#008dcc',
  accent: '#FDC304',
  pink: '#F90B8A',
  purple: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
}

/**
 * Visual tone matrix showing the three personality dimensions
 * with 1-5 scale bars
 */
export function PersonalityMatrixDiagram() {
  const dimensions = [
    {
      label: 'Warmth',
      icon: Heart,
      color: brandColors.pink,
      low: 'Clinical',
      high: 'Nurturing',
      description: 'Emotional temperature of responses',
    },
    {
      label: 'Formality',
      icon: Crown,
      color: brandColors.purple,
      low: 'Casual',
      high: 'Ceremonious',
      description: 'Register and linguistic structure',
    },
    {
      label: 'Humor',
      icon: Smile,
      color: brandColors.accent,
      low: 'Serious',
      high: 'Playful',
      description: 'Lightness and wit in delivery',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          The Tone Matrix
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Three dimensions, scored 1-5, that quantify agent personality
        </p>
        <div className='space-y-5'>
          {dimensions.map(dim => {
            const Icon = dim.icon
            return (
              <div
                key={dim.label}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                <div className='flex items-center gap-3 mb-3'>
                  <div
                    className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: `${dim.color}15` }}
                  >
                    <Icon
                      className='w-4.5 h-4.5'
                      style={{ color: dim.color }}
                    />
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-foreground'>
                      {dim.label}
                    </p>
                    <p className='text-[11px] text-muted-foreground'>
                      {dim.description}
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-[10px] text-muted-foreground w-16 text-right flex-shrink-0'>
                    {dim.low}
                  </span>
                  <div className='flex-1 flex gap-1.5'>
                    {[1, 2, 3, 4, 5].map(level => (
                      <div
                        key={level}
                        className='flex-1 h-7 rounded-md flex items-center justify-center'
                        style={{
                          backgroundColor: `${dim.color}${level <= 3 ? level * 8 + 5 : level * 12}`,
                        }}
                      >
                        <span
                          className='text-[10px] font-bold'
                          style={{
                            color: level >= 4 ? '#fff' : dim.color,
                          }}
                        >
                          {level}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span className='text-[10px] text-muted-foreground w-16 flex-shrink-0'>
                    {dim.high}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Shows the five personality archetypes on a spectrum
 */
export function ArchetypeSpectrumDiagram() {
  const archetypes = [
    {
      name: 'Expert',
      icon: GraduationCap,
      color: brandColors.primary,
      warmth: '2-3',
      formality: '4-5',
      humor: '1-2',
      bestFor: 'Technical support, finance, legal',
    },
    {
      name: 'Guide',
      icon: Compass,
      color: brandColors.green,
      warmth: '4-5',
      formality: '2-3',
      humor: '2-3',
      bestFor: 'Onboarding, education, tutorials',
    },
    {
      name: 'Collaborator',
      icon: Handshake,
      color: brandColors.purple,
      warmth: '3-4',
      formality: '1-2',
      humor: '3-4',
      bestFor: 'Creative tools, project management',
    },
    {
      name: 'Concierge',
      icon: ConciergeBell,
      color: brandColors.pink,
      warmth: '4-5',
      formality: '3-4',
      humor: '1-2',
      bestFor: 'E-commerce, hospitality, scheduling',
    },
    {
      name: 'Coach',
      icon: Trophy,
      color: brandColors.accent,
      warmth: '3-4',
      formality: '1-2',
      humor: '2-3',
      bestFor: 'Fitness, training, development',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          Five Personality Archetypes
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Each archetype maps to specific tone matrix ranges and use cases
        </p>
        <div className='grid grid-cols-1 gap-3'>
          {archetypes.map(arch => {
            const Icon = arch.icon
            return (
              <div
                key={arch.name}
                className='bg-background border border-border/60 rounded-xl p-4 flex items-start gap-3'
              >
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${arch.color}15` }}
                >
                  <Icon className='w-5 h-5' style={{ color: arch.color }} />
                </div>
                <div className='flex-1 min-w-0'>
                  <p
                    className='text-sm font-semibold'
                    style={{ color: arch.color }}
                  >
                    The {arch.name}
                  </p>
                  <p className='text-[11px] text-muted-foreground mt-0.5'>
                    {arch.bestFor}
                  </p>
                  <div className='flex gap-3 mt-2'>
                    {[
                      { label: 'W', value: arch.warmth },
                      { label: 'F', value: arch.formality },
                      { label: 'H', value: arch.humor },
                    ].map(metric => (
                      <div
                        key={metric.label}
                        className='flex items-center gap-1'
                      >
                        <span className='text-[10px] text-muted-foreground font-medium'>
                          {metric.label}:
                        </span>
                        <span
                          className='text-[10px] font-bold px-1.5 py-0.5 rounded'
                          style={{
                            backgroundColor: `${arch.color}15`,
                            color: arch.color,
                          }}
                        >
                          {metric.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <p className='text-[11px] text-muted-foreground text-center mt-4'>
          W = Warmth, F = Formality, H = Humor (1-5 scale)
        </p>
      </div>
    </div>
  )
}

/**
 * Shows the four calibration signals and how they adjust tone
 */
export function ToneCalibrationDiagram() {
  const signals = [
    {
      label: 'User Sentiment',
      icon: Thermometer,
      color: brandColors.pink,
      adjustments: [
        {
          trigger: 'Frustrated',
          rule: 'Warmth +1, Humor -2, Lead with acknowledgment',
        },
        { trigger: 'Excited', rule: 'Match energy, Formality drops' },
        {
          trigger: 'Confused',
          rule: 'Warmth +1, Shorter sentences, Offer restart',
        },
      ],
    },
    {
      label: 'Conversation Stage',
      icon: MessageCircle,
      color: brandColors.primary,
      adjustments: [
        {
          trigger: 'Early (1-3)',
          rule: 'Listen mode, Higher warmth, Build safety',
        },
        {
          trigger: 'Mid (4-8)',
          rule: 'Assert more, Share opinions, Collaborate',
        },
        { trigger: 'Late (9+)', rule: 'Increase directness, Offer summary' },
      ],
    },
    {
      label: 'Task Complexity',
      icon: Gauge,
      color: brandColors.purple,
      adjustments: [
        {
          trigger: 'Simple',
          rule: 'Minimal personality, The answer IS the experience',
        },
        {
          trigger: 'Complex',
          rule: 'More scaffolding, Empathy, Status updates',
        },
      ],
    },
    {
      label: 'Error States',
      icon: AlertTriangle,
      color: brandColors.red,
      adjustments: [
        { trigger: 'Agent error', rule: 'Warmth +1 (not +2), Humor to zero' },
        {
          trigger: 'User error',
          rule: 'Never blame, "Let me try differently"',
        },
        {
          trigger: 'System error',
          rule: 'Concrete next step, No excessive apology',
        },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-2 text-center'>
          Context-Aware Tone Calibration
        </h3>
        <p className='text-xs text-muted-foreground mb-6 text-center'>
          Four signals that dynamically adjust personality while maintaining
          voice identity
        </p>
        <div className='space-y-4'>
          {signals.map(signal => {
            const Icon = signal.icon
            return (
              <div
                key={signal.label}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                <div className='flex items-center gap-2.5 mb-3'>
                  <div
                    className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                    style={{ backgroundColor: `${signal.color}15` }}
                  >
                    <Icon className='w-4 h-4' style={{ color: signal.color }} />
                  </div>
                  <p
                    className='text-sm font-semibold'
                    style={{ color: signal.color }}
                  >
                    {signal.label}
                  </p>
                </div>
                <div className='space-y-1.5'>
                  {signal.adjustments.map(adj => (
                    <div
                      key={adj.trigger}
                      className='flex items-start gap-2 pl-2'
                    >
                      <span
                        className='text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5'
                        style={{
                          backgroundColor: `${signal.color}15`,
                          color: signal.color,
                        }}
                      >
                        {adj.trigger}
                      </span>
                      <span className='text-[11px] text-foreground/80'>
                        {adj.rule}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
